import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import ConfirmDialog from '../components/ConfirmDialog';
import { useLanguage } from './LanguageContext';
import { useTheme } from './ThemeContext';

// Volet B-1 — confirmation centrale promise-based (remplace window.confirm).
// useConfirm() retourne confirm(options) => Promise<boolean> :
//   true  = bouton confirmer
//   false = annuler / croix / backdrop / ESC
// Un seul <ConfirmDialog/> est monté par le Provider (symétrie ToastProvider).
// options = { title?, message, confirmText?, cancelText?, variant? }.
// Labels par défaut i18n (common.delete / common.cancel) + isDark via useTheme.

const ConfirmContext = createContext(null);

export const useConfirm = () => {
  const context = useContext(ConfirmContext);
  if (!context) {
    throw new Error('useConfirm must be used within a ConfirmProvider');
  }
  return context;
};

const INITIAL_STATE = { isOpen: false, options: {} };

export const ConfirmProvider = ({ children }) => {
  const { t } = useLanguage();
  const { isDark } = useTheme();
  const [state, setState] = useState(INITIAL_STATE);
  // Resolver hors state : la Promise est résolue par un side-effect propre
  // (pas dans un updater setState, évite tout double-appel en StrictMode).
  const resolverRef = useRef(null);

  const confirm = useCallback((options = {}) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setState({ isOpen: true, options });
    });
  }, []);

  const settle = useCallback((result) => {
    if (resolverRef.current) {
      resolverRef.current(result);
      resolverRef.current = null;
    }
    setState(INITIAL_STATE);
  }, []);

  const handleConfirm = useCallback(() => settle(true), [settle]);
  const handleCancel = useCallback(() => settle(false), [settle]);

  const { title, message, confirmText, cancelText, variant } = state.options;

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}
      <ConfirmDialog
        isOpen={state.isOpen}
        onConfirm={handleConfirm}
        onClose={handleCancel}
        title={title ?? t('common.confirm')}
        message={message}
        confirmText={confirmText ?? t('common.delete')}
        cancelText={cancelText ?? t('common.cancel')}
        variant={variant ?? 'danger'}
        isDark={isDark}
      />
    </ConfirmContext.Provider>
  );
};
